/**
 * Утилиты валидации для защиты от инъекций
 */
import DOMPurify from 'dompurify';
// Паттерны для обнаружения потенциально опасного контента
const DANGEROUS_PATTERNS = {
    // SQL инъекции
    sql: /('|"|;|--|\/\*|\*\/|xp_|sp_|0x|union\s+select|select\s+.*\s+from|insert\s+into|update\s+.*\s+set|delete\s+from|drop\s+table|alter\s+table|exec\s*\(|execute\s*\()/i,
    // XSS атаки
    xss: /<script|<\/script|javascript:|on\w+\s*=|<iframe|<object|<embed|<svg\s+onload|expression\s*\(|vbscript:|data:/i,
    // Командные инъекции
    command: /[;&|`$]|\$\(|`.*`|\|\||&&/,
    // Path traversal
    path: /\.\.[\/\\]|~\/|\/etc\/|\/proc\//,
};
/**
 * Проверка на SQL инъекции
 */
export const hasSqlInjection = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return DANGEROUS_PATTERNS.sql.test(value);
};
/**
 * Проверка на XSS
 */
export const hasXssInjection = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return DANGEROUS_PATTERNS.xss.test(value);
};
/**
 * Проверка на командные инъекции
 */
export const hasCommandInjection = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return DANGEROUS_PATTERNS.command.test(value);
};
/**
 * Проверка на path traversal
 */
export const hasPathTraversal = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return DANGEROUS_PATTERNS.path.test(value);
};
/**
 * Комплексная проверка на все виды инъекций
 */
export const hasInjection = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return hasSqlInjection(value) || hasXssInjection(value) || hasCommandInjection(value) || hasPathTraversal(value);
};
/**
 * Санитизация строки - удаление потенциально опасных символов
 */
export const sanitizeString = (value) => {
    if (!value || typeof value !== 'string')
        return '';
    return value
        .replace(/[<>]/g, '') // Удаляем HTML теги
        .replace(/['";]/g, '') // Удаляем кавычки и точку с запятой
        .trim();
};
/**
 * Санитизация HTML с помощью DOMPurify
 * Удаляет опасные скрипты и атрибуты, оставляя безопасную разметку
 */
export const sanitizeHtml = (html) => {
    if (!html || typeof html !== 'string')
        return '';
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span', 'div', 'pre', 'code'],
        ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
        ALLOW_DATA_ATTR: false,
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
    });
};
/**
 * Валидация URL - проверка на безопасный протокол
 */
export const isValidUrl = (url) => {
    if (!url || typeof url !== 'string')
        return false;
    try {
        const parsed = new URL(url);
        // Разрешаем только безопасные протоколы
        return ['https:', 'http:', 'tg:', 'mailto:'].includes(parsed.protocol);
    }
    catch {
        return false;
    }
};
/**
 * Экранирование для CSV - предотвращение formula injection
 */
export const escapeCsvValue = (value) => {
    if (!value || typeof value !== 'string')
        return '';
    // Если начинается с потенциально опасного символа, добавляем апостроф
    if (/^[=+\-@\t\r]/.test(value)) {
        return "'" + value;
    }
    return value;
};
/**
 * Валидация логина
 * Разрешены: буквы, цифры, подчеркивание, точка, дефис
 */
export const isValidLogin = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    // return /^[a-zA-Z0-9_.-]{3,50}$/.test(value)
    return true && !hasInjection(value);
};
/**
 * Валидация имени пользователя
 * Разрешены: буквы (включая кириллицу), цифры, пробелы, дефис
 */
export const isValidUserName = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return /^[\p{L}\p{N}\s-]{2,100}$/u.test(value) && !hasInjection(value);
};
/**
 * Валидация email
 */
export const isValidEmail = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value) && value.length <= 100;
};
/**
 * Валидация пароля
 * Минимум 4 символа, без опасных паттернов
 */
export const isValidPassword = (value) => {
    if (!value || typeof value !== 'string')
        return false;
    return value.length >= 4 && value.length <= 100 && !hasXssInjection(value);
};
/**
 * Валидация телефона
 * Разрешены: цифры, +, -, пробелы, скобки
 */
export const isValidPhone = (value) => {
    if (!value || typeof value !== 'string')
        return true; // Поле опциональное
    if (value === '')
        return true;
    return /^[0-9+\-\s()]{5,20}$/.test(value);
};
/**
 * Валидация Telegram username
 * Разрешены: буквы, цифры, подчеркивание (начинается с @)
 */
export const isValidTelegramUsername = (value) => {
    if (!value || typeof value !== 'string')
        return true; // Поле опциональное
    if (value === '')
        return true;
    // Может начинаться с @ или без
    const username = value.startsWith('@') ? value.slice(1) : value;
    return /^[a-zA-Z0-9_]{4,32}$/.test(username);
};
/**
 * Правила валидации для Quasar q-input
 */
export const validationRules = {
    login: [
        (val) => !!val || 'Login is required',
        (val) => val.length >= 3 || 'Login must be at least 3 characters',
        (val) => val.length <= 50 || 'Login must be less than 50 characters',
        (val) => isValidLogin(val) || 'Login can only contain letters, numbers, underscore, dot and hyphen',
        (val) => !hasInjection(val) || 'Invalid characters detected',
    ],
    userName: [
        (val) => !!val || 'Name is required',
        (val) => val.length >= 2 || 'Name must be at least 2 characters',
        (val) => val.length <= 100 || 'Name must be less than 100 characters',
        (val) => !hasInjection(val) || 'Invalid characters detected',
    ],
    email: [
        (val) => !!val || 'Email is required',
        (val) => isValidEmail(val) || 'Invalid email format',
        (val) => !hasInjection(val) || 'Invalid characters detected',
    ],
    password: [
        (val) => !!val || 'Password is required',
        (val) => val.length >= 4 || 'Password must be at least 4 characters',
        (val) => val.length <= 100 || 'Password must be less than 100 characters',
        (val) => !hasXssInjection(val) || 'Invalid characters detected',
    ],
    phone: [
        (val) => !val || isValidPhone(val) || 'Invalid phone format',
        (val) => !val || !hasInjection(val) || 'Invalid characters detected',
    ],
    telegramUsername: [
        (val) => !val || isValidTelegramUsername(val) || 'Invalid Telegram username format',
        (val) => !val || !hasInjection(val) || 'Invalid characters detected',
    ],
};
export default {
    hasSqlInjection,
    hasXssInjection,
    hasCommandInjection,
    hasPathTraversal,
    hasInjection,
    sanitizeString,
    sanitizeHtml,
    isValidLogin,
    isValidUserName,
    isValidEmail,
    isValidPassword,
    isValidPhone,
    isValidTelegramUsername,
    isValidUrl,
    escapeCsvValue,
    validationRules,
};
